import React, { useRef, useState, DragEvent, useEffect } from 'react';
import { Col, Row } from 'react-bootstrap';
import { Label } from 'reactstrap';
import MSelect from '../../components/MSelect';
import { useAppDispatch, useAppSelector } from '../../store/redux-hooks';
import { SelectOptionType } from '../../utils/Types';
import {
  fetchLeadTypeBranchDrops,
  leadSelector,
} from '../../features/leadSlice';

type Status = { loading?: boolean; message?: string };

type ValidateResult = {
  canImport: boolean;
  message?: string;
};

type Props = {
  handleDownload: () => void;
  // IMPORTANT: Your parent can still do e.target.files?.[0]
  onFileChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  // Called when user presses Upload (you receive the selected file)
  onSubmit?: (file: File) => Promise<void> | void;
  // Called when user presses Validate (returns canImport + optional message)
  onValidate?: (file: File) => Promise<ValidateResult>;
  // Called on Cancel (we also clear local selection and errors)
  onCancel?: () => void;
  status?: Status; // controls upload loading + message color rule
  maxSizeBytes?: number; // default 10 MB
  disabled?: boolean;
};

const BulkUploadBox: React.FC<Props> = ({
  handleDownload,
  onFileChange,
  onSubmit,
  onValidate, // <-- NEW
  onCancel,
  status,
  maxSizeBytes = 10 * 1024 * 1024,
  disabled = false,
}) => {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const [error, setError] = useState<string>('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  // NEW: validation state
  const [validating, setValidating] = useState(false);
  const [canImport, setCanImport] = useState<boolean | null>(null);
  const [validationMsg, setValidationMsg] = useState<string>('');
  const [selectedBank, setSelectedBank] = useState<number>();
  const [selectedBranch, setSelectedBranch] = useState<number>();
  const leadTypeBankOptions: SelectOptionType[] =
    useAppSelector(leadSelector).leadTypeBankOptions;
  const leadTypeBranchOptions: SelectOptionType[] =
    useAppSelector(leadSelector).leadTypeBranchOptions;

  const dispatch = useAppDispatch();

  const [leadBankTypeDropOptions, setLeadBankTypeDropOptions] = useState<
    SelectOptionType[]
  >([]);
  const [leadBranchTypeDropOptions, setLeadBranchTypeDropOptions] = useState<
    SelectOptionType[]
  >([]);

  const accept =
    '.xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel';

  const validateLocal = (file: File) => {
    const name = file.name.toLowerCase();
    const isExcel =
      name.endsWith('.xlsx') ||
      file.type ===
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
    if (!isExcel) return 'Please choose a valid Excel file (.xlsx).';
    if (file.size > maxSizeBytes)
      return `File is too large. Max ${(maxSizeBytes / (1024 * 1024)).toFixed(0)} MB allowed.`;
    return '';
  };

  /** Converts a File into an event-like object so parent can keep using e.target.files */
  const callParentOnFileChangeWithEvent = (file: File) => {
    const dt = new DataTransfer();
    dt.items.add(file);
    if (inputRef.current) {
      try {
        inputRef.current.files = dt.files; // reflect selection inside the hidden input
      } catch {
        // ignore if browser blocks programmatic assignment
      }
    }
    const fakeEvent = {
      target: { files: dt.files },
      currentTarget: { files: dt.files },
    } as unknown as React.ChangeEvent<HTMLInputElement>;
    onFileChange(fakeEvent);
  };

  // Reset validation whenever file changes
  const resetValidation = () => {
    setCanImport(null);
    setValidationMsg('');
  };

  const setFileIfValid = (file?: File) => {
    if (!file) return;
    const v = validateLocal(file);
    if (v) {
      setError(v);
      setSelectedFile(null);
      resetValidation();
      return;
    }
    setError('');
    setSelectedFile(file);
    resetValidation();
    callParentOnFileChangeWithEvent(file);
  };

  const onInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const v = validateLocal(f);
    if (v) {
      setError(v);
      setSelectedFile(null);
      resetValidation();
      return;
    }
    setError('');
    setSelectedFile(f);
    resetValidation();
    // Pass original event so parent code using e.target.files still works
    onFileChange(e);
  };

  const onDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
    const f = e.dataTransfer.files?.[0];
    setFileIfValid(f);
  };

  const onDragOverHandler = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(true);
  };

  const onDragLeaveHandler = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
  };

  const clearSelection = () => {
    setSelectedFile(null);
    setError('');
    resetValidation();
    if (inputRef.current) inputRef.current.value = '';
    onCancel?.();
  };

  const handleValidate = async () => {
    // Bank validation
    if (!selectedBank) {
      setValidationMsg('Please select a bank.');
      return;
    }
    // Branch validation
    if (!selectedBranch) {
      setValidationMsg('Please select a branch.');
      return;
    }
    if (!selectedFile || !onValidate) return;
    try {
      setValidating(true);
      setValidationMsg('');
      setCanImport(null);
      const result = await onValidate(selectedFile);
      setCanImport(!!result?.canImport);
      setValidationMsg(
        result?.message ||
          (result?.canImport
            ? 'Validation passed. Ready to upload.'
            : 'Validation failed.')
      );
    } catch (err: any) {
      setCanImport(false);
      setValidationMsg(err?.message || 'Validation failed.');
    } finally {
      setValidating(false);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile || status?.loading) return;
    // Prevent upload when validation didn’t pass
    if (canImport !== true) {
      setValidationMsg('Please validate the file before uploading.');
      return;
    }
    await onSubmit?.(selectedFile);
  };

  // Inline styles
  const styles = {
    dropzone: {
      marginTop: 10,
      border: `2px dashed ${dragOver ? '#60A5FA' : '#9CA3AF'}`,
      borderRadius: 8,
      background: dragOver ? '#F0F7FF' : '#FBFBFB',
      transition: 'background 0.15s ease, border-color 0.15s ease',
      minHeight: 160,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: 24,
      textAlign: 'center' as const,
      position: 'relative' as const,
      cursor: 'pointer',
      outline: 'none',
    },
    iconBox: {
      position: 'absolute' as const,
      top: 12,
      left: 12,
      background: '#EEF2FF',
      border: '1px solid #DBEAFE',
      borderRadius: 10,
      padding: 6,
    },
    line1: { fontSize: 16, color: '#111827' },
    line2: { fontSize: 12, color: '#6B7280', margin: '6px 0' },
    browseBtn: {
      border: '1px solid #2563EB',
      color: '#2563EB',
      background: '#FFFFFF',
      borderRadius: 6,
      padding: '8px 14px',
      fontSize: 14,
      cursor: 'pointer',
      opacity: status?.loading ? 0.6 : 1,
    },
    helper: { marginTop: 8, color: '#6B7280', fontSize: 12 },
    error: { marginTop: 8, color: '#DC2626', fontSize: 13 },
    status: { marginTop: 12, fontSize: 13 },
    actionsBar: {
      display: 'flex',
      gap: 10,
      justifyContent: 'flex-start',
      padding: 12,
      marginTop: 16,
      borderTop: '1px solid #E5E7EB',
      background: '#F3F4F6',
      borderRadius: 8,
    },
    primaryBtn: {
      background: '#2563EB',
      color: '#FFFFFF',
      border: 'none',
      borderRadius: 6,
      padding: '8px 16px',
      fontSize: 14,
      cursor: 'pointer',
      opacity: status?.loading ? 0.6 : 1,
    },
    secondaryBtn: {
      background: '#FFFFFF',
      color: '#2563EB',
      border: '1px solid #2563EB',
      borderRadius: 6,
      padding: '8px 16px',
      fontSize: 14,
      cursor: 'pointer',
      opacity: validating ? 0.7 : 1,
    },
    ghostBtn: {
      background: '#E5E7EB',
      color: '#111827',
      border: 'none',
      borderRadius: 6,
      padding: '8px 16px',
      fontSize: 14,
      cursor: 'pointer',
    },
    picked: { marginTop: 10, color: '#374151', fontSize: 13 },
    link: { color: '#2563EB', textDecoration: 'none' as const },
    chip: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: 8,
      border: '1px solid #D1D5DB',
      background: '#F9FAFB',
      borderRadius: 9999,
      padding: '6px 10px',
      marginTop: 8,
    },
    chipName: {
      maxWidth: 360,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap' as const,
      fontSize: 13,
    },
    chipRemove: {
      border: 'none',
      background: 'transparent',
      color: '#6B7280',
      fontSize: 16,
      lineHeight: 1,
      cursor: 'pointer',
    },
  };

  // Derived disabled states
  const noFile = !selectedFile;
  const uploading = !!status?.loading;

  const validateDisabled = noFile || validating || uploading;
  const uploadDisabled =
    noFile || validating || uploading || canImport !== true;
  const cancelDisabled = noFile || validating || uploading;

  useEffect(() => {
    setLeadBranchTypeDropOptions(leadTypeBranchOptions);
  }, [leadTypeBranchOptions]);

  useEffect(() => {
    setLeadBankTypeDropOptions(leadTypeBankOptions);
  }, [leadTypeBankOptions]);

  return (
    <div
      style={{
        opacity: disabled ? 0.5 : 1,
        pointerEvents: disabled ? 'none' : 'auto',
      }}
    >
      <label className="header-style">Bulk Upload</label>

      <div className="mt-1">
        The upload file format should be{' '}
        <a
          href="#"
          onClick={(e) => {
            e.preventDefault();
            handleDownload();
          }}
          style={styles.link}
        >
          Download users.xlsx
        </a>
      </div>
      <div className="mt-3">
        <Label className="header-style">Branch Lead Details</Label>
        <Row>
          <Col xs={12} md={4}>
            <MSelect
              label="Bank Name"
              required
              disabled={disabled}
              options={leadBankTypeDropOptions}
              value={selectedBank ?? ''}
              onChange={(e) => {
                const bankId = Number(e.target.value);
                setSelectedBank(bankId);
                dispatch(fetchLeadTypeBranchDrops({ bankId }));
              }}
            />
          </Col>

          <Col xs={12} md={4}>
            <MSelect
              label="Branch Name"
              required
              disabled={disabled}
              options={leadBranchTypeDropOptions}
              value={selectedBranch ?? ''}
              onChange={(e) => {
                setSelectedBranch(Number(e.target.value));
              }}
            />
          </Col>
        </Row>
      </div>

      <div className="mt-3">
        <label className="header-style">Choose Excel file (.xlsx)</label>

        {/* Drag / Browse design box */}
        <div
          style={styles.dropzone}
          onDrop={onDrop}
          onDragOver={onDragOverHandler}
          onDragLeave={onDragLeaveHandler}
          role="button"
          tabIndex={0}
          aria-label="Drop Excel file here or browse to upload"
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') inputRef.current?.click();
          }}
          onClick={() => inputRef.current?.click()}
        >
          {/* small icon (optional) */}
          <div style={styles.iconBox} aria-hidden="true">
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
              <path
                d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6z"
                stroke="#3B82F6"
                strokeWidth="1.5"
                fill="#fff"
              />
              <path d="M14 2v6h6" stroke="#3B82F6" strokeWidth="1.5" />
              <path
                d="M12 17v-6m0 0-2 2m2-2 2 2"
                stroke="#3B82F6"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>

          <div>
            <div style={styles.line1}>Drag your file(s) to start uploading</div>
            <div style={styles.line2}>OR</div>
            <button
              type="button"
              style={styles.browseBtn}
              onClick={(e) => {
                e.stopPropagation();
                inputRef.current?.click();
              }}
              disabled={uploading}
            >
              Browse Files
            </button>

            {/* Hidden input for browsing */}
            <input
              id="excelInput"
              ref={inputRef}
              type="file"
              accept={accept}
              onChange={onInputChange}
              hidden
            />
          </div>
        </div>

        {/* File chip preview */}
        {selectedFile && (
          <div
            style={styles.chip}
            title={`${selectedFile.name} • ${(selectedFile.size / 1024).toFixed(0)} KB`}
          >
            <span style={styles.chipName}>
              {selectedFile.name} • {(selectedFile.size / 1024).toFixed(0)} KB
            </span>
            <button
              type="button"
              style={styles.chipRemove}
              aria-label="Remove selected file"
              onClick={clearSelection}
              disabled={cancelDisabled}
            >
              ×
            </button>
          </div>
        )}

        {/* Helper text / errors */}
        <p style={styles.helper}>
          Only supports <strong>.xlsx</strong> files (Max file size:{' '}
          {(maxSizeBytes / (1024 * 1024)).toFixed(0)} MB)
        </p>

        {error && <p style={styles.error}>{error}</p>}

        {/* Validation feedback */}
        {validationMsg && (
          <p
            style={{
              ...styles.status,
              color: canImport ? 'green' : 'crimson',
            }}
          >
            {validationMsg}
          </p>
        )}

        {/* Footer actions */}
        <div style={styles.actionsBar}>
          {/* Validate */}
          <button
            type="button"
            style={styles.secondaryBtn}
            onClick={handleValidate}
            disabled={validateDisabled}
            aria-disabled={validateDisabled}
          >
            {validating ? 'Validating…' : 'Validate'}
          </button>

          {/* Upload */}

          <button
            type="button"
            onClick={handleUpload}
            disabled={uploadDisabled}
            style={{
              ...styles.primaryBtn,
              opacity: uploadDisabled ? 0.5 : 1,
              cursor: uploadDisabled ? 'not-allowed' : 'pointer',
              background: uploadDisabled ? '#9CA3AF' : '#2563EB',
            }}
          >
            {status?.loading ? 'Uploading…' : 'Upload'}
          </button>

          {/* Cancel */}
          <button
            type="button"
            style={styles.ghostBtn}
            onClick={clearSelection}
            disabled={cancelDisabled}
            aria-disabled={cancelDisabled}
          >
            Cancel
          </button>
        </div>

        {/* Existing status message (upload result) */}
        {status?.message && (
          <p
            style={{
              ...styles.status,
              color: status.message.startsWith('Error') ? 'crimson' : 'green',
            }}
          >
            {status.message}
          </p>
        )}
      </div>
    </div>
  );
};

export default BulkUploadBox;
